import { CartRepository, CreateCartData, UpdateCartData, GetCartData, CartFilter, CreateCartItemData, GetCartItemData } from '../repository/cart.repository';

export class CartUseCase {
  constructor(private cartRepository: CartRepository) {
    console.log('[CartUseCase] Constructor - Cart repository initialized:', !!this.cartRepository);
  }

  // ===== CART MANAGEMENT =====

  async createCart(cartData: CreateCartData): Promise<GetCartData> {
    console.log('[CartUseCase.createCart] START - CartData:', cartData);
    try {
      // Validate cart data
      if (!cartData.userId) {
        throw new Error('User ID is required');
      }

      if (cartData.totalAmount < 0) {
        throw new Error('Total amount cannot be negative');
      }

      // Check if user already has an active cart
      const existingCart = await this.cartRepository.getUserActiveCart(cartData.userId);
      if (existingCart && cartData.status === 'active') {
        console.log('[CartUseCase.createCart] User already has active cart, returning existing cart');
        return existingCart;
      }

      const newCart = await this.cartRepository.createCart(cartData);
      console.log('[CartUseCase.createCart] SUCCESS - Created cart:', newCart.id);
      return newCart;
    } catch (error) {
      console.error('[CartUseCase.createCart] ERROR:', error);
      throw error;
    }
  }

  async getCartById(cartId: string): Promise<GetCartData | null> {
    console.log('[CartUseCase.getCartById] START - CartId:', cartId);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      const cart = await this.cartRepository.getCartById(cartId);
      console.log('[CartUseCase.getCartById] SUCCESS - Cart found:', !!cart);
      return cart;
    } catch (error) {
      console.error('[CartUseCase.getCartById] ERROR:', error);
      throw error;
    }
  }

  async getCarts(filter: CartFilter = {}, limitCount?: number): Promise<GetCartData[]> {
    console.log('[CartUseCase.getCarts] START - Filter:', filter, 'Limit:', limitCount);
    try {
      const carts = await this.cartRepository.getCarts(filter, limitCount);
      console.log('[CartUseCase.getCarts] SUCCESS - Retrieved', carts.length, 'carts');
      return carts;
    } catch (error) {
      console.error('[CartUseCase.getCarts] ERROR:', error);
      throw error;
    }
  }

  async getAllCarts(limitCount?: number): Promise<GetCartData[]> {
    console.log('[CartUseCase.getAllCarts] START - Limit:', limitCount);
    try {
      const carts = await this.cartRepository.getAllCarts(limitCount);
      console.log('[CartUseCase.getAllCarts] SUCCESS - Retrieved', carts.length, 'carts');
      return carts;
    } catch (error) {
      console.error('[CartUseCase.getAllCarts] ERROR:', error);
      throw error;
    }
  }

  async updateCart(cartId: string, updateData: UpdateCartData): Promise<GetCartData> {
    console.log('[CartUseCase.updateCart] START - CartId:', cartId, 'UpdateData:', updateData);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      // Validate update data
      if (updateData.totalAmount !== undefined && updateData.totalAmount < 0) {
        throw new Error('Total amount cannot be negative');
      }

      const updatedCart = await this.cartRepository.updateCart(cartId, updateData);
      console.log('[CartUseCase.updateCart] SUCCESS - Cart updated');
      return updatedCart;
    } catch (error) {
      console.error('[CartUseCase.updateCart] ERROR:', error);
      throw error;
    }
  }

  async deleteCart(cartId: string): Promise<void> {
    console.log('[CartUseCase.deleteCart] START - CartId:', cartId);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      await this.cartRepository.deleteCart(cartId);
      console.log('[CartUseCase.deleteCart] SUCCESS - Cart deleted');
    } catch (error) {
      console.error('[CartUseCase.deleteCart] ERROR:', error);
      throw error;
    }
  }

  async getUserActiveCart(userId: string): Promise<GetCartData | null> {
    console.log('[CartUseCase.getUserActiveCart] START - UserId:', userId);
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const activeCart = await this.cartRepository.getUserActiveCart(userId);
      console.log('[CartUseCase.getUserActiveCart] SUCCESS - Active cart found:', !!activeCart);
      return activeCart;
    } catch (error) {
      console.error('[CartUseCase.getUserActiveCart] ERROR:', error);
      throw error;
    }
  }

  // ===== CART ITEM MANAGEMENT =====

  async addItemToCart(cartId: string, itemData: Omit<CreateCartItemData, 'cartId'>): Promise<GetCartItemData> {
    console.log('[CartUseCase.addItemToCart] START - CartId:', cartId, 'ItemData:', itemData);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      // Validate item data
      if (!itemData.productId || !itemData.productName) {
        throw new Error('Product ID and name are required');
      }

      if (itemData.quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
      }

      if (itemData.unitPrice < 0) {
        throw new Error('Unit price cannot be negative');
      }

      // Check if cart exists
      const cart = await this.cartRepository.getCartById(cartId);
      if (!cart) {
        throw new Error('Cart not found');
      }

      if (cart.status !== 'active') {
        throw new Error('Cannot add items to inactive cart');
      }

      // Create cart item
      const newItem = await this.cartRepository.addCartItem({
        cartId: cartId,
        ...itemData
      });

      // Update cart total
      const cartItems = await this.cartRepository.getCartItems(cartId);
      const newTotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
      
      await this.cartRepository.updateCart(cartId, {
        totalAmount: newTotal + (cart.taxAmount || 0) + (cart.shippingAmount || 0) - (cart.discountAmount || 0)
      });

      console.log('[CartUseCase.addItemToCart] SUCCESS - Item added to cart');
      return newItem;
    } catch (error) {
      console.error('[CartUseCase.addItemToCart] ERROR:', error);
      throw error;
    }
  }

  async updateCartItem(itemId: string, updateData: Partial<CreateCartItemData>): Promise<GetCartItemData> {
    console.log('[CartUseCase.updateCartItem] START - ItemId:', itemId, 'UpdateData:', updateData);
    try {
      if (!itemId) {
        throw new Error('Item ID is required');
      }

      // Get the item first to validate cart status
      const item = await this.cartRepository.getCartItemById(itemId);
      if (!item) {
        throw new Error('Cart item not found');
      }

      const cart = await this.cartRepository.getCartById(item.cartId);
      if (!cart || cart.status !== 'active') {
        throw new Error('Cannot update items in inactive cart');
      }

      // Validate update data
      if (updateData.quantity !== undefined && updateData.quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
      }

      if (updateData.unitPrice !== undefined && updateData.unitPrice < 0) {
        throw new Error('Unit price cannot be negative');
      }

      const updatedItem = await this.cartRepository.updateCartItem(itemId, updateData);

      // Recalculate cart total
      const cartItems = await this.cartRepository.getCartItems(item.cartId);
      const newSubtotal = cartItems.reduce((sum, cartItem) => sum + cartItem.totalPrice, 0);
      
      await this.cartRepository.updateCart(item.cartId, {
        totalAmount: newSubtotal + (cart.taxAmount || 0) + (cart.shippingAmount || 0) - (cart.discountAmount || 0)
      });

      console.log('[CartUseCase.updateCartItem] SUCCESS - Cart item updated');
      return updatedItem;
    } catch (error) {
      console.error('[CartUseCase.updateCartItem] ERROR:', error);
      throw error;
    }
  }

  async removeItemFromCart(itemId: string): Promise<void> {
    console.log('[CartUseCase.removeItemFromCart] START - ItemId:', itemId);
    try {
      if (!itemId) {
        throw new Error('Item ID is required');
      }

      // Get the item first to get cart ID
      const item = await this.cartRepository.getCartItemById(itemId);
      if (!item) {
        throw new Error('Cart item not found');
      }

      const cart = await this.cartRepository.getCartById(item.cartId);
      if (!cart || cart.status !== 'active') {
        throw new Error('Cannot remove items from inactive cart');
      }

      await this.cartRepository.removeCartItem(itemId);

      // Recalculate cart total
      const remainingItems = await this.cartRepository.getCartItems(item.cartId);
      const newSubtotal = remainingItems.reduce((sum, cartItem) => sum + cartItem.totalPrice, 0);
      
      await this.cartRepository.updateCart(item.cartId, {
        totalAmount: newSubtotal + (cart.taxAmount || 0) + (cart.shippingAmount || 0) - (cart.discountAmount || 0)
      });

      console.log('[CartUseCase.removeItemFromCart] SUCCESS - Item removed from cart');
    } catch (error) {
      console.error('[CartUseCase.removeItemFromCart] ERROR:', error);
      throw error;
    }
  }

  async getCartItems(cartId: string): Promise<GetCartItemData[]> {
    console.log('[CartUseCase.getCartItems] START - CartId:', cartId);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      const items = await this.cartRepository.getCartItems(cartId);
      console.log('[CartUseCase.getCartItems] SUCCESS - Retrieved', items.length, 'items');
      return items;
    } catch (error) {
      console.error('[CartUseCase.getCartItems] ERROR:', error);
      throw error;
    }
  }

  async clearCart(cartId: string): Promise<void> {
    console.log('[CartUseCase.clearCart] START - CartId:', cartId);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      const cart = await this.cartRepository.getCartById(cartId);
      if (!cart) {
        throw new Error('Cart not found');
      }

      await this.cartRepository.deleteAllCartItems(cartId);
      
      // Reset cart total
      await this.cartRepository.updateCart(cartId, {
        totalAmount: (cart.taxAmount || 0) + (cart.shippingAmount || 0) - (cart.discountAmount || 0)
      });

      console.log('[CartUseCase.clearCart] SUCCESS - Cart cleared');
    } catch (error) {
      console.error('[CartUseCase.clearCart] ERROR:', error);
      throw error;
    }
  }

  // ===== CART CONVERSION =====

  async convertCartToPurchase(cartId: string): Promise<{ success: boolean; purchaseId?: string }> {
    console.log('[CartUseCase.convertCartToPurchase] START - CartId:', cartId);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      const cart = await this.cartRepository.getCartById(cartId);
      if (!cart) {
        throw new Error('Cart not found');
      }

      if (cart.status !== 'active') {
        throw new Error('Can only convert active carts');
      }

      const cartItems = await this.cartRepository.getCartItems(cartId);
      if (cartItems.length === 0) {
        throw new Error('Cannot convert empty cart');
      }

      // Mark cart as completed
      await this.cartRepository.updateCart(cartId, { status: 'completed' });

      console.log('[CartUseCase.convertCartToPurchase] SUCCESS - Cart marked as completed');
      return { success: true };
    } catch (error) {
      console.error('[CartUseCase.convertCartToPurchase] ERROR:', error);
      throw error;
    }
  }

  async abandonCart(cartId: string): Promise<void> {
    console.log('[CartUseCase.abandonCart] START - CartId:', cartId);
    try {
      if (!cartId) {
        throw new Error('Cart ID is required');
      }

      await this.cartRepository.updateCart(cartId, { status: 'abandoned' });
      console.log('[CartUseCase.abandonCart] SUCCESS - Cart marked as abandoned');
    } catch (error) {
      console.error('[CartUseCase.abandonCart] ERROR:', error);
      throw error;
    }
  }
}